import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Search, Camera, MapPin, Calendar, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PB_LOGO = "/brand/pb-logo.png";
const BG = "https://images.unsplash.com/photo-1509928015542-fcc9b3bcd048?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

function AlbumCard({ a, i }) {
  return (
    <Link to={`/gallery/${a.id}`} data-testid={`gallery-result-${a.id}`}
      className="group relative block border border-zinc-800 bg-zinc-950 overflow-hidden hover:border-[#A3E635] transition-colors duration-200 animate-fade-up"
      style={{ animationDelay: `${i * 60}ms` }}>
      <div className="aspect-[16/10] overflow-hidden bg-zinc-900">
        {a.cover_url ? (
          <img src={a.cover_url} alt={a.title} loading="lazy"
            className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700"><Camera className="w-10 h-10" /></div>
        )}
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 pt-10">
        <div className="font-display font-black uppercase text-lg leading-tight text-white">{a.title}</div>
        <div className="flex flex-wrap gap-3 mt-1 text-[10px] uppercase tracking-widest text-zinc-400">
          {a.date && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{a.date}</span>}
          {a.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{a.location}</span>}
          <span className="flex items-center gap-1 text-[#A3E635]"><Camera className="w-3 h-3" />{a.media_count} shots</span>
        </div>
      </div>
      <span className="absolute top-3 right-3 w-8 h-8 border border-white/20 bg-black/60 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <ArrowRight className="w-4 h-4 text-[#A3E635]" />
      </span>
    </Link>
  );
}

export default function GalleryLanding() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => { document.title = "Tournament Gallery — Pickle Bullpen"; }, []);
  useEffect(() => { axios.get(`${API}/gallery/featured`).then((r) => setFeatured(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    setSearching(true);
    const tm = setTimeout(() => {
      axios.get(`${API}/gallery/search`, { params: { q: q.trim() } })
        .then((r) => setResults(r.data)).catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(tm);
  }, [q]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white relative overflow-hidden" data-testid="gallery-landing">
      <div className="absolute inset-0">
        <img src={BG} alt="" className="w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/85 to-[#0a0a0a]" />
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 py-4">
        <a href="/" className="flex items-center gap-3">
          <img src={PB_LOGO} alt="Pickle Bullpen" className="w-11 h-11 object-contain" />
          <span className="font-display font-black uppercase tracking-wide text-sm">Pickle Bullpen</span>
        </a>
        <a href="/auth" data-testid="gallery-join-link"
          className="border border-[#A3E635]/60 text-[#A3E635] text-[10px] font-black uppercase tracking-widest px-4 py-2 hover:bg-[#A3E635] hover:text-black transition-colors">
          Join the Bullpen
        </a>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 pt-[10vh] pb-20">
        <div className="text-[10px] font-black uppercase tracking-[0.4em] text-[#22D3EE] mb-3 animate-fade-up">Tournament Gallery</div>
        <h1 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] animate-fade-up" style={{ animationDelay: "80ms" }}>
          Find Yourself<br /><span className="text-[#A3E635]">On The Wall.</span>
        </h1>
        <p className="text-zinc-400 text-sm md:text-base mt-4 max-w-xl animate-fade-up" style={{ animationDelay: "160ms" }}>
          Every tournament, every battle, every celebration — captured. Search a tournament name below to pull up its gallery, relive the moments, and share them with the world.
        </p>

        <div className="mt-8 animate-fade-up" style={{ animationDelay: "240ms" }}>
          <div className="flex items-center gap-3 border-2 border-zinc-700 bg-black/80 backdrop-blur-md px-5 py-4 focus-within:border-[#A3E635] transition-colors">
            <Search className="w-6 h-6 text-[#A3E635] shrink-0" />
            <input data-testid="gallery-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Type a tournament name… e.g. Summer Slam"
              className="flex-1 bg-transparent text-lg md:text-xl text-white placeholder-zinc-600 focus:outline-none font-display uppercase tracking-wide" />
          </div>
          {featured.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="gallery-featured-row">
              <span className="text-[9px] uppercase tracking-[0.3em] text-zinc-500 font-bold">Featured:</span>
              {featured.map((a) => (
                <Link key={a.id} to={`/gallery/${a.id}`} data-testid={`gallery-featured-${a.id}`}
                  className="border border-[#22D3EE]/40 text-[#22D3EE] text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 hover:bg-[#22D3EE]/10 transition-colors">
                  {a.title}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10">
          {results === null ? (
            <div className="border border-dashed border-zinc-800 bg-black/40 backdrop-blur-sm p-10 text-center" data-testid="gallery-instructions">
              <Camera className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
              <div className="font-display font-black uppercase text-xl text-zinc-400">Search a tournament to open its gallery</div>
              <p className="text-zinc-600 text-sm mt-2 max-w-md mx-auto">
                Start typing above — matching tournaments appear instantly. Tap one to see the full photo & video wall, drop a like, and share your favorite shots on Facebook.
              </p>
            </div>
          ) : searching ? (
            <div className="text-center text-zinc-500 py-10 animate-pulse font-display uppercase">Searching…</div>
          ) : results.length === 0 ? (
            <div className="text-center text-zinc-500 py-10 font-display uppercase" data-testid="gallery-no-results">
              No galleries match "{q}" yet — check the spelling or try another event.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="gallery-results-grid">
              {results.map((a, i) => <AlbumCard key={a.id} a={a} i={i} />)}
            </div>
          )}
        </div>
      </main>

      <footer className="relative z-10 border-t border-zinc-900 px-6 py-4 text-[11px] text-zinc-600 flex flex-wrap justify-between gap-2">
        <span>© Pickle Bullpen — Performance Ecosystem</span>
        <a href="/welcome" className="hover:text-[#A3E635] transition-colors">What is Pickle Bullpen?</a>
      </footer>
    </div>
  );
}
